import React, { useEffect, useMemo, useRef, useState } from "react";
import "./BulkEditPopup.css";
import { useBulkEditOptions } from "./useBulkEditOptions";
import { DatePickerNew } from "../../../../Components/InvoiceEditor/Components/CommonUI/DatePicker/DatePickerNew";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Option {
  value: string;
  label: string;
  /** supplier / category id (same ids the single-document edit form uses) */
  id?: number | null;
}

/** Option sets come from the same sources the single-document edit form uses. */
export interface BulkEditOptions {
  type: Option[];
  supplier: Option[];
  category: Option[];
  currency: Option[];
  paid: Option[];
  paymentMethod: Option[];
}

/** "" always means "No change" / untouched. */
export interface BulkEditFormState {
  type: string;
  invoiceDate: string;
  supplier: string;
  category: string;
  description: string;
  currency: string;
  paid: string;
  paymentMethod: string;
 
}

/** Payload sent to the API: ONLY the fields the user actually changed. */
export interface BulkEditPayload {
  type?: string;
  invoiceDate?: string;
  supplier?: string;
  supplierId?: number;
  category?: string;
  categoryId?: number;
  description?: string;
  currency?: string;
  paid?: string;
  paymentMethod?: string;
 
}

const EMPTY_FORM: BulkEditFormState = {
  type: "",
  invoiceDate: "",
  supplier: "",
  category: "",
  description: "",
  currency: "",
  paid: "",
  paymentMethod: "",

};


type SelectKey =
  | "type"
  | "supplier"
  | "category"
  | "currency"
  | "paid"
  | "paymentMethod";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const validate = (
  f: BulkEditFormState,
  maxDescriptionLength: number,
  allowFutureDates: boolean,
): Partial<Record<keyof BulkEditFormState, string>> => {
  const errors: Partial<Record<keyof BulkEditFormState, string>> = {};

  if (f.invoiceDate) {
    const d = new Date(f.invoiceDate);
    if (Number.isNaN(d.getTime())) {
      errors.invoiceDate = "Enter a valid date.";
    } else if (!allowFutureDates && d.getTime() > Date.now()) {
      errors.invoiceDate = "Date cannot be in the future.";
    }
  }

  if (f.description.length > maxDescriptionLength) {
    errors.description = `Description must be ${maxDescriptionLength} characters or fewer.`;
  }

  return errors;
};

/**
 * Build a payload containing only touched fields.
 * Needs the option lists so supplier/category ids can be looked up,
 * the same way the edit form's buildSavePayload does.
 */
const buildPayload = (
  f: BulkEditFormState,
  o: BulkEditOptions,
): BulkEditPayload => {
  const p: BulkEditPayload = {};
  if (f.type) p.type = f.type;
  if (f.invoiceDate) p.invoiceDate = f.invoiceDate;

  if (f.supplier) {
    p.supplier = f.supplier;
    const id = o.supplier.find((x) => x.value === f.supplier)?.id;
    if (id != null) p.supplierId = id;
  }

  if (f.category) {
    p.category = f.category;
    const id = o.category.find((x) => x.value === f.category)?.id;
    if (id != null) p.categoryId = id;
  }

  if (f.description.trim()) p.description = f.description.trim();
  if (f.currency) p.currency = f.currency;
  if (f.paid) p.paid = f.paid;
  if (f.paymentMethod) p.paymentMethod = f.paymentMethod;
 
  return p;
};

// ─── Bulk Edit Popup ─────────────────────────────────────────────────────────

interface BulkEditPopupProps {
  count: number;
  loading: boolean;
  /** Server / network error. Modal stays open and the primary button becomes "Retry". */
  error: string | null;
  maxDescriptionLength?: number;
  allowFutureDates?: boolean;
  onCancel: () => void;
  onSubmit: (payload: BulkEditPayload) => void;
}

const NO_CHANGE = "No change";

const BulkEditPopup: React.FC<BulkEditPopupProps> = ({
  count,
  loading,
  error,
  maxDescriptionLength = 500,
  allowFutureDates = true,
  onCancel,
  onSubmit,
}) => {
  // Dropdown data is loaded here (suppliers/categories from the API,
  // everything else from the shared constants used by the edit form).
  const options = useBulkEditOptions();

  const [form, setForm] = useState<BulkEditFormState>(EMPTY_FORM);
  const [showTooltip, setShowTooltip] = useState(false);

  const errors = useMemo(
    () => validate(form, maxDescriptionLength, allowFutureDates),
    [form, maxDescriptionLength, allowFutureDates],
  );
  const hasErrors = Object.keys(errors).length > 0;
  const payload = useMemo(() => buildPayload(form, options), [form, options]);
  const isDirty = Object.keys(payload).length > 0;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, loading]);

  const set =
    (key: keyof BulkEditFormState) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const canSubmit = isDirty && !hasErrors && !loading;

  const handleSubmit = () => {
    if (canSubmit) onSubmit(payload);
  };

  const renderSelect = (
    key: SelectKey,
    label: string,
    opts: Option[],
    labelExtra?: React.ReactNode,
  ) => {
    const id = `be-${key}`;
    const changed = form[key] !== "";
    return (
      <div className={`be-field${changed ? " be-field--changed" : ""}`}>
        <label className="be-label" htmlFor={id}>
          {label}
          {labelExtra}
        </label>
        <select
          id={id}
          className="be-input"
          value={form[key]}
          onChange={set(key)}
          disabled={loading}
        >
          <option value="">{NO_CHANGE}</option>
          {opts.map((o) => (
            <option key={`${o.id ?? ""}-${o.value}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const supplierInfo = (
    <span className="be-info-wrap">
      <button
        type="button"
        className="be-info"
        aria-label="Supplier change information"
        aria-describedby="be-supplier-tip"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        onClick={() => setShowTooltip((v) => !v)}
      >
        i
      </button>
      <span
        id="be-supplier-tip"
        role="tooltip"
        className="be-tooltip"
        hidden={!showTooltip}
      >
        Changing the supplier will overwrite any supplier rules currently
        applied to these documents
      </span>
    </span>
  );

  return (
    <div
      className="tm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="be-title"
    >
      <div
        className="tm-backdrop"
        onClick={loading ? undefined : onCancel}
      />

      <div className="tm-modal be-modal">
        <div className="tm-modal-header">
          <h2 className="tm-modal-title" id="be-title">
            Bulk edit {count} {count === 1 ? "item" : "items"}
          </h2>
          <button
            className="tm-modal-close"
            onClick={onCancel}
            disabled={loading}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="tm-modal-body">
          <p className="be-hint">
            Only fields you change will be updated. Fields set to “{NO_CHANGE}”
            or left blank keep each document’s current value.
          </p>

          {error && (
            <div className="be-error-banner" role="alert">
              {error}
            </div>
          )}

          <div className="be-grid">
            {renderSelect("type", "Type", options.type)}

            <div
              className={`be-field be-datepicker-field${form.invoiceDate ? " be-field--changed" : ""}`}
            >
              <label className="be-label" htmlFor="invoiceDate">
                Date
              </label>
              <DatePickerNew
                disabled={loading}
                inputType="text"
                name="invoiceDate"
                value={form.invoiceDate}
                label="Date"
                errorMessage={errors.invoiceDate}
                iserror={!!errors.invoiceDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((prev) => ({ ...prev, invoiceDate: e.target.value }))
                }
                onBlur={() => { }}
              />
            </div>

            {renderSelect(
              "supplier",
              "Supplier",
              options.supplier,
              supplierInfo,
            )}
            {renderSelect("category", "Category", options.category)}

            <div
              className={`be-field be-field--wide${form.description ? " be-field--changed" : ""}`}
            >
              <label className="be-label" htmlFor="be-description">
                Description
              </label>
              <textarea
                id="be-description"
                className="be-input"
                rows={1}
                value={form.description}
                onChange={set("description")}
                disabled={loading}
                maxLength={
                  maxDescriptionLength + 1 /* let validation show the message */
                }
                placeholder="Leave blank to keep current descriptions"
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? "be-desc-err" : undefined
                }
              />
              {errors.description && (
                <span id="be-desc-err" className="be-field-error">
                  {errors.description}
                </span>
              )}
            </div>

            {renderSelect("currency", "Currency", options.currency)}
            {renderSelect("paid", "Paid", options.paid)}
            {renderSelect(
              "paymentMethod",
              "Payment Method",
              options.paymentMethod,
            )}

            
          </div>
        </div>

        <div className="tm-modal-footer">
          <button
            className="tm-btn tm-btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="tm-btn tm-btn-continue"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-busy={loading}
          >
            {loading ? "Updating…" : error ? "Retry" : "Bulk Edit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditPopup;

// ─── Toast ───────────────────────────────────────────────────────────────────

export interface BulkEditToastState {
  variant: "success" | "partial";
  message: string;
  failedIds?: number[];
}

interface BulkEditToastProps extends BulkEditToastState {
  onClose: () => void;
  onViewFailed?: (ids: number[]) => void;
  autoDismissMs?: number;
}

export const BulkEditToast: React.FC<BulkEditToastProps> = ({
  variant,
  message,
  failedIds = [],
  onClose,
  onViewFailed,
  autoDismissMs,
}) => {
  // Success: 5 seconds (per the requirement). Partial failure: 10 seconds,
  // so the user has time to click "View failed items".
  const dismissAfter = autoDismissMs ?? (variant === "success" ? 5000 : 10000);

  // Keep the latest onClose in a ref, so a parent re-render
  // (which creates a new onClose function) doesn't restart the timer.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const timer = window.setTimeout(() => onCloseRef.current(), dismissAfter);
    return () => window.clearTimeout(timer);
  }, [dismissAfter]);

  return (
    <div
      className={`be-toast be-toast--${variant}`}
      role={variant === "success" ? "status" : "alert"}
      aria-live={variant === "success" ? "polite" : "assertive"}
    >
      <span className="be-toast-msg">{message}</span>
      {/* {variant === "partial" && failedIds.length > 0 && onViewFailed && (
        <button
          className="be-toast-link"
          onClick={() => {
            onViewFailed(failedIds);
            onClose();
          }}
        >
          View failed items
        </button>
      )} */}
      <button
        className="be-toast-close"
        onClick={onClose}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  );
};